import { html, css, LitElement } from 'lit-element';
import {
  doc, getDoc, updateDoc, deleteDoc, deleteField,
  collection, query, where, getDocs, arrayRemove,
} from 'firebase/firestore';

import '@material/mwc-button';
import '@material/mwc-dialog';
import '@material/mwc-textfield';
import '@material/mwc-checkbox';
import '@material/mwc-radio';
import '@material/mwc-formfield';

import { db } from './intrivity_app.js';
import { hasRole, callSuperadminEndpoint } from './utility.js';
import { t, Localized } from './i18n.js';
import { AUDIT, recordAudit } from './audit_log.js';

// The categories deleteUserData (functions/retention.js) can erase
// independently. Mirrors PERSONAL_DATA there -- keep the ids (and their
// removal.category.* strings in locales/) in
// sync if that list changes. mail-queue is deliberately left off: it's
// internal hygiene ("never cleaned today"), not something an admin is
// removing on the user's behalf.
// Labels are i18n keys: removal.category.<id> (and .detail where present).
const FULL_ERASURE_CATEGORIES = [
  { id: 'user-profile', detail: true },
  { id: 'assessment-report' },
  { id: 'raw-survey-response' },
  { id: 'invitations' },
  { id: 'group-roles' },
  { id: 'client-roles' },
];
const KNOWN_CATEGORIES = new Set(FULL_ERASURE_CATEGORIES.map((c) => c.id));
// The server's preview also names locations outside this list; those keep the
// server's English label.
const categoryLabel = (id, fallback) => (KNOWN_CATEGORIES.has(id) ? t(`removal.category.${id}`) : fallback);

// Superadmin-only "Remove Data" dialog for a selected user, opened from
// admin_users.js next to Masquerade/Edit. Two independent modes:
//
//   - Remove from selected groups/teams: keeps the account, survey response
//     and report, only unwinds membership in the chosen groups/teams (done
//     directly against Firestore -- security rules already permit staff to
//     make each of these writes, same as the existing per-group Remove
//     button in intrivity-admin-users.js; this dialog is what additionally
//     clears the AI summary cache and cascades to child teams).
//   - Remove all data: calls the existing deleteuserdata Cloud Function
//     (functions/retention.js), which is the only thing that can actually
//     recursiveDelete the user doc and scan Storage -- security rules can't
//     express that, so it has to be a server call.
export class IntrivityUserDataRemovalDialog extends Localized(LitElement) {

  static get properties() {
    return {
      user_profile: { type: Object },
      open: { type: Boolean },
      targetUser: { type: Object },
      _loading: { type: Boolean },
      _freshUser: { type: Object },
      _groupsInfo: { type: Array },
      _mode: { type: String },
      _selectedGroupIds: { type: Object },
      _selectedCategories: { type: Object },
      _confirmEmail: { type: String },
      _previewResult: { type: Object },
      _previewSignature: { type: String },
      _previewLoading: { type: Boolean },
      _working: { type: Boolean },
      _resultMessage: { type: String },
      _errorMessage: { type: String },
    };
  }

  constructor() {
    super();
    this.open = false;
    this.targetUser = null;
    this._loading = false;
    this._freshUser = null;
    this._groupsInfo = [];
    this._mode = 'groups';
    this._selectedGroupIds = new Set();
    this._selectedCategories = new Set(FULL_ERASURE_CATEGORIES.map((c) => c.id));
    this._confirmEmail = '';
    this._previewResult = null;
    this._previewSignature = null;
    this._previewLoading = false;
    this._working = false;
    this._resultMessage = null;
    this._errorMessage = null;
    this._loadedForEmail = null;
  }

  updated(changedProps) {
    if (this.open && this.targetUser && this.targetUser.email
        && this._loadedForEmail !== this.targetUser.email) {
      this._loadedForEmail = this.targetUser.email;
      this._loadTargetUser();
    }
    if (changedProps.has('open') && !this.open) {
      this._loadedForEmail = null;
    }
  }

  async _loadTargetUser() {
    this._loading = true;
    this._mode = 'groups';
    this._selectedGroupIds = new Set();
    this._selectedCategories = new Set(FULL_ERASURE_CATEGORIES.map((c) => c.id));
    this._confirmEmail = '';
    this._previewResult = null;
    this._previewSignature = null;
    this._resultMessage = null;
    this._errorMessage = null;

    const email = this.targetUser.email;
    try {
      const snap = await getDoc(doc(db, 'users', email));
      const freshUser = snap.exists() ? { ...snap.data(), _id: snap.id } : { email };
      this._freshUser = freshUser;

      const groupIds = freshUser.groups || [];
      const groupsInfo = await Promise.all(groupIds.map(async (uuid) => {
        const [groupSnap, aiSnap] = await Promise.all([
          getDoc(doc(db, 'groups', uuid)),
          getDoc(doc(db, 'users', email, 'ai_summaries', uuid)),
        ]);
        const data = groupSnap.exists() ? groupSnap.data() : {};
        return {
          uuid,
          name: data.name || uuid,
          type: data.type || 'top',
          parent: data.parent || null,
          hasAiSummary: aiSnap.exists(),
        };
      }));
      this._groupsInfo = groupsInfo;
    } catch (e) {
      console.error('Could not load user for removal dialog:', e);
      this._errorMessage = t('removal.loadFailed');
    } finally {
      this._loading = false;
    }
  }

  _toggleGroupSelection(uuid, checked) {
    const next = new Set(this._selectedGroupIds);
    if (checked) {
      next.add(uuid);
      // Selecting a top-level group also selects the teams under it this
      // person belongs to -- the "corresponding teams underneath" case.
      // The admin can still uncheck an individual team afterward.
      const info = this._groupsInfo.find((g) => g.uuid === uuid);
      if (info && info.type !== 'team') {
        this._groupsInfo
          .filter((g) => g.type === 'team' && g.parent === uuid)
          .forEach((t) => next.add(t.uuid));
      }
    } else {
      next.delete(uuid);
    }
    this._selectedGroupIds = next;
    this._previewResult = null;
  }

  _toggleCategory(id, checked) {
    const next = new Set(this._selectedCategories);
    if (checked) next.add(id); else next.delete(id);
    this._selectedCategories = next;
    this._previewResult = null;
  }

  _currentSignature() {
    return this._mode === 'groups'
      ? `groups:${[...this._selectedGroupIds].sort().join(',')}`
      : `full:${[...this._selectedCategories].sort().join(',')}`;
  }

  async _runPreview() {
    this._errorMessage = null;
    this._previewLoading = true;
    try {
      if (this._mode === 'groups') {
        const items = this._groupsInfo.filter((g) => this._selectedGroupIds.has(g.uuid));
        this._previewResult = { mode: 'groups', items };
      } else {
        const email = this.targetUser.email;
        const report = await callSuperadminEndpoint('inventoryuserdata', { email });
        const locations = report.locations.filter((l) => this._selectedCategories.has(l.id));
        this._previewResult = { mode: 'full', locations };
      }
      this._previewSignature = this._currentSignature();
    } catch (e) {
      this._errorMessage = e.message;
    } finally {
      this._previewLoading = false;
    }
  }

  _canConfirm() {
    if (this._working) return false;
    if (this._previewSignature !== this._currentSignature()) return false;
    const email = this.targetUser && this.targetUser.email;
    if (!email || this._confirmEmail.trim() !== email) return false;
    return this._mode === 'groups'
      ? this._selectedGroupIds.size > 0
      : this._selectedCategories.size > 0;
  }

  async _confirmRemove() {
    if (!hasRole('superadmin', this.user_profile)) return;
    if (!this._canConfirm()) return;
    this._working = true;
    this._errorMessage = null;
    try {
      if (this._mode === 'groups') {
        await this._removeFromGroups();
      } else {
        await this._eraseAll();
      }
    } catch (e) {
      this._errorMessage = e.message;
    } finally {
      this._working = false;
    }
  }

  async _removeFromGroups() {
    const email = this.targetUser.email;
    const ids = [...this._selectedGroupIds];
    const userRef = doc(db, 'users', email);

    const updatePayload = { groups: arrayRemove(...ids), updated_at: new Date().toISOString() };
    if (this._freshUser.group && ids.includes(this._freshUser.group)) {
      updatePayload.group = deleteField();
    }
    await updateDoc(userRef, updatePayload);

    const results = [];
    for (const id of ids) {
      try {
        const rolesSnap = await getDocs(query(
          collection(db, 'group_roles'),
          where('group_uuid', '==', id),
          where('email', '==', email),
        ));
        await Promise.all(rolesSnap.docs.map((d) => deleteDoc(doc(db, 'group_roles', d.id))));

        await deleteDoc(doc(db, 'groups', id, 'invitations', email));
        await deleteDoc(doc(db, 'users', email, 'ai_summaries', id));
        await updateDoc(doc(db, 'groups', id), { status: 'process' });

        results.push({ id, ok: true });
      } catch (e) {
        results.push({ id, ok: false, error: e.message });
      }
    }

    await recordAudit(AUDIT.USER_DATA_REMOVE_GROUPS, { subject: email, detail: { groups: ids, results } });

    const failedCount = results.filter((r) => !r.ok).length;
    this._resultMessage = failedCount
      ? t('removal.groupsPartial', { done: results.length - failedCount, total: results.length, failed: failedCount })
      : t('removal.groupsDone', { count: results.length });
    if (failedCount) console.error('Group removal failures:', results.filter((r) => !r.ok));

    this.dispatchEvent(new CustomEvent('user-data-removed', {
      detail: { email, mode: 'groups', groups: ids }, bubbles: true, composed: true,
    }));
  }

  async _eraseAll() {
    const email = this.targetUser.email;
    const categories = [...this._selectedCategories].join(',');
    const result = await callSuperadminEndpoint('deleteuserdata', {
      email, categories, confirm: 'ERASE', confirm_email: this._confirmEmail.trim(),
    });

    this._resultMessage = [
      t('removal.erased', { count: result.removed.length }),
      result.failed.length ? t('removal.eraseFailed', { count: result.failed.length }) : '',
      result.reprocessed.length ? t('removal.reprocessed', { count: result.reprocessed.length }) : '',
      result.reprocessFailed.length ? t('removal.reprocessFailed', { count: result.reprocessFailed.length }) : '',
    ].filter(Boolean).join(' ');

    this.dispatchEvent(new CustomEvent('user-data-removed', {
      detail: { email, mode: 'full', result }, bubbles: true, composed: true,
    }));
  }

  _close() {
    this.open = false;
    this.dispatchEvent(new CustomEvent('dialog-closed', { bubbles: true, composed: true }));
  }

  _renderGroupsMode() {
    const topLevel = this._groupsInfo.filter((g) => g.type !== 'team');
    const orphanTeams = this._groupsInfo.filter((g) => g.type === 'team'
      && !this._groupsInfo.some((p) => p.uuid === g.parent));

    if (!this._groupsInfo.length) {
      return html`<p>${t('removal.noGroups')}</p>`;
    }

    const renderRow = (g, indented) => html`
      <div style="${indented ? 'margin-left: 24px;' : ''}">
        <mwc-formfield label="${g.name} ${g.type === 'team' ? t('removal.teamSuffix') : ''}${g.hasAiSummary ? t('removal.hasSummary') : ''}">
          <mwc-checkbox
            .checked=${this._selectedGroupIds.has(g.uuid)}
            @change=${(e) => this._toggleGroupSelection(g.uuid, e.target.checked)}>
          </mwc-checkbox>
        </mwc-formfield>
      </div>
    `;

    return html`
      ${topLevel.map((g) => html`
        ${renderRow(g, false)}
        ${this._groupsInfo.filter((t) => t.type === 'team' && t.parent === g.uuid)
          .map((t) => renderRow(t, true))}
      `)}
      ${orphanTeams.map((t) => renderRow(t, false))}
    `;
  }

  _renderFullMode() {
    return html`
      ${FULL_ERASURE_CATEGORIES.map((c) => html`
        <div>
          <mwc-formfield label="${t(`removal.category.${c.id}`)}">
            <mwc-checkbox
              .checked=${this._selectedCategories.has(c.id)}
              @change=${(e) => this._toggleCategory(c.id, e.target.checked)}>
            </mwc-checkbox>
          </mwc-formfield>
          ${c.detail ? html`<div class="detail">${t(`removal.category.${c.id}.detail`)}</div>` : html``}
        </div>
      `)}
      <p class="detail">${t('removal.retained')}</p>
    `;
  }

  _renderPreview() {
    if (!this._previewResult) return html``;
    if (this._previewResult.mode === 'groups') {
      return html`
        <div class="preview">
          <strong>${t('removal.willRemove')}</strong>
          <ul>
            ${this._previewResult.items.map((g) => html`
              <li>${g.name}${g.type === 'team' ? ' ' + t('removal.teamSuffix') : ''}${g.hasAiSummary ? t('removal.clearsSummary') : ''}</li>
            `)}
          </ul>
        </div>
      `;
    }
    return html`
      <div class="preview">
        <strong>${t('removal.willErase')}</strong>
        <ul>
          ${this._previewResult.locations.map((l) => html`<li>${t('removal.locationCount', { label: categoryLabel(l.id, l.label), count: l.count })}</li>`)}
        </ul>
      </div>
    `;
  }

  render() {
    const email = this.targetUser && this.targetUser.email;

    return html`
      <mwc-dialog heading=${t('removal.heading')} ?open=${this.open} @closed=${() => this._close()}>
        ${email ? html`<p><strong>${this.targetUser.displayName || email}</strong> (${email})</p>` : html``}

        ${this._loading ? html`<p>${t('common.loading')}</p>` : html`
          <div>
            <mwc-formfield label=${t('removal.modeGroups')}>
              <mwc-radio name="mode" .checked=${this._mode === 'groups'}
                @change=${() => { this._mode = 'groups'; this._previewResult = null; }}></mwc-radio>
            </mwc-formfield>
            <mwc-formfield label=${t('removal.modeFull')}>
              <mwc-radio name="mode" .checked=${this._mode === 'full'}
                @change=${() => { this._mode = 'full'; this._previewResult = null; }}></mwc-radio>
            </mwc-formfield>
          </div>

          <div class="scroll-region">
            ${this._mode === 'groups' ? this._renderGroupsMode() : this._renderFullMode()}

            <div style="margin-top: 10px;">
              <mwc-button outlined label=${t('removal.preview')} ?disabled=${this._previewLoading} @click=${() => this._runPreview()}></mwc-button>
              ${this._previewLoading ? html`<span>${t('removal.loadingPreview')}</span>` : html``}
            </div>

            ${this._renderPreview()}
          </div>

          <mwc-textfield
            style="width: 100%; margin-top: 10px;"
            label=${t('removal.confirmLabel')}
            .value=${this._confirmEmail}
            @input=${(e) => { this._confirmEmail = e.target.value; }}>
          </mwc-textfield>

          ${this._errorMessage ? html`<p class="error">${this._errorMessage}</p>` : html``}
          ${this._resultMessage ? html`<p class="result">${this._resultMessage}</p>` : html``}
        `}

        <mwc-button
          slot="primaryAction"
          label=${this._working ? t('removal.removing') : t('adminUsers.removeData')}
          ?disabled=${!this._canConfirm()}
          @click=${() => this._confirmRemove()}>
        </mwc-button>
        <mwc-button slot="secondaryAction" dialogAction="cancel" label=${this._resultMessage ? t('common.close') : t('common.cancel')}></mwc-button>
      </mwc-dialog>
    `;
  }

  static get styles() {
    return css`
      /* mwc-dialog only recalculates its own scroll region when it opens, not
         when content added afterward (e.g. a Preview result) grows past the
         viewport -- so the checklist/preview get their own bounded,
         independently-scrolling area instead, keeping the confirm field and
         action buttons always reachable regardless of how much content is
         above them. */
      .scroll-region { max-height: 40vh; overflow-y: auto; }
      .detail { font-size: 0.85em; color: #666; margin: 0 0 8px 32px; }
      .preview { margin-top: 10px; padding: 8px; background: #f5f5f5; border-radius: 4px; }
      .error { color: #b00020; }
      .result { color: #1a6b1a; }
    `;
  }
}
if (!customElements.get('intrivity-user-data-removal-dialog')) customElements.define('intrivity-user-data-removal-dialog', IntrivityUserDataRemovalDialog);
